export const VARIANT_FILTER_KEYS = ["talle", "color", "material"] as const

export type VariantFilterKey = (typeof VARIANT_FILTER_KEYS)[number]

export type VariantFilterState = Record<VariantFilterKey, string[]>
export type VariantFilterFacets = Record<VariantFilterKey, string[]>
type VariantFilterValueSets = Record<VariantFilterKey, string[]>

type SearchParamsRecord = Record<string, unknown>

const OPTION_NAME_TO_FILTER_KEY: Record<string, VariantFilterKey> = {
  talle: "talle",
  color: "color",
  material: "material",
}

const createEmptyMaps = () => ({
  talle: new Map<string, string>(),
  color: new Map<string, string>(),
  material: new Map<string, string>(),
})

export const createEmptyVariantFilterState = (): VariantFilterState => ({
  talle: [],
  color: [],
  material: [],
})

export const createEmptyVariantFilterFacets = (): VariantFilterFacets => ({
  talle: [],
  color: [],
  material: [],
})

const normalizeText = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/\s*([/,|])\s*/g, "$1")
    .replace(/\s+/g, " ")
    .toLowerCase()

const toDisplayValue = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return trimmed.length ? trimmed : undefined
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value)
  }

  return undefined
}

const shouldReplaceDisplayValue = (current: string, next: string) => {
  const currentHasUpper = /[A-ZÁÉÍÓÚÑ]/.test(current)
  const nextHasUpper = /[A-ZÁÉÍÓÚÑ]/.test(next)
  if (nextHasUpper && !currentHasUpper) {
    return true
  }

  const currentHasDigits = /\d/.test(current)
  const nextHasDigits = /\d/.test(next)
  if (nextHasDigits && !currentHasDigits) {
    return true
  }

  return false
}

const upsertCaseInsensitive = (map: Map<string, string>, value: string) => {
  const normalized = normalizeText(value)
  if (!normalized.length) {
    return
  }

  const current = map.get(normalized)
  if (!current) {
    map.set(normalized, value)
    return
  }

  if (shouldReplaceDisplayValue(current, value)) {
    map.set(normalized, value)
  }
}

const normalizeOptionNameToFilterKey = (
  value: unknown
): VariantFilterKey | undefined => {
  if (typeof value !== "string") {
    return undefined
  }

  const normalized = normalizeText(value)
  if (!normalized.length) {
    return undefined
  }

  return OPTION_NAME_TO_FILTER_KEY[normalized]
}

const extractOptionValue = (value: unknown): string | undefined => {
  if (typeof value === "string" || typeof value === "number") {
    return toDisplayValue(value)
  }

  if (value && typeof value === "object") {
    const candidateValue = (value as Record<string, unknown>).value
    if (
      typeof candidateValue === "string" ||
      typeof candidateValue === "number"
    ) {
      return toDisplayValue(candidateValue)
    }

    const candidateOptionValue = (value as Record<string, unknown>).option_value
    if (
      typeof candidateOptionValue === "string" ||
      typeof candidateOptionValue === "number"
    ) {
      return toDisplayValue(candidateOptionValue)
    }
  }

  return undefined
}

const splitCommaValues = (value: unknown) => {
  if (Array.isArray(value)) {
    return value
      .flatMap((entry) => String(entry).split(","))
      .map((entry) => entry.trim())
      .filter(Boolean)
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean)
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const serialized = String(value).trim()
    return serialized ? [serialized] : []
  }

  return []
}

const createEmptyValueSets = (): VariantFilterValueSets => ({
  talle: [],
  color: [],
  material: [],
})

const toFiniteStock = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value
  }

  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  return undefined
}

const isProductAvailableWithoutVariant = (product: unknown) => {
  const source = product as Record<string, unknown> | null | undefined
  if (!source || typeof source !== "object") {
    return false
  }

  if (source.trackInventory !== true) {
    return true
  }

  const stock = toFiniteStock(source.stock)
  return typeof stock === "number" ? stock > 0 : false
}

const isVariantAvailable = (product: unknown, variant: unknown) => {
  const productRecord = product as Record<string, unknown> | null | undefined
  const variantRecord = variant as Record<string, unknown> | null | undefined

  if (!variantRecord || typeof variantRecord !== "object") {
    return false
  }

  const trackInventory =
    typeof variantRecord.trackInventory === "boolean"
      ? variantRecord.trackInventory
      : productRecord?.trackInventory

  if (trackInventory !== true) {
    return true
  }

  const stock = toFiniteStock(variantRecord.stock)
  return typeof stock === "number" ? stock > 0 : false
}

const addValueToSets = (
  sets: VariantFilterValueSets,
  filterKey: VariantFilterKey,
  value: unknown
) => {
  const display = extractOptionValue(value)
  if (!display) {
    return
  }

  const map = new Map<string, string>()
  sets[filterKey].forEach((entry) => {
    map.set(normalizeText(entry), entry)
  })
  upsertCaseInsensitive(map, display)
  sets[filterKey] = Array.from(map.values())
}

const addOptionValuesToSets = (
  sets: VariantFilterValueSets,
  optionName: unknown,
  rawValues: unknown
) => {
  const filterKey = normalizeOptionNameToFilterKey(optionName)
  if (!filterKey) {
    return
  }

  const values = Array.isArray(rawValues) ? rawValues : [rawValues]
  values.forEach((value) => addValueToSets(sets, filterKey, value))
}

const buildValueSetsFromProductOptions = (product: unknown) => {
  const sets = createEmptyValueSets()

  const options = Array.isArray((product as any)?.options)
    ? ((product as any).options as Array<Record<string, unknown>>)
    : []

  options.forEach((option) => {
    addOptionValuesToSets(sets, option?.name ?? option?.title, option?.values)
  })

  return sets
}

const addLegacyVariantOptionToSets = (
  sets: VariantFilterValueSets,
  option: Record<string, unknown>
) => {
  const optionName =
    option?.option_name ??
    option?.name ??
    option?.title ??
    option?.option_id ??
    option?.optionId ??
    option?.id ??
    (option?.option as Record<string, unknown> | undefined)?.name ??
    (option?.option as Record<string, unknown> | undefined)?.title ??
    (option?.option as Record<string, unknown> | undefined)?.id
  const optionValue =
    option?.option_value ??
    option?.value ??
    (option?.option as Record<string, unknown> | undefined)?.value

  addOptionValuesToSets(sets, optionName, optionValue)
}

const buildValueSetsFromVariant = (variant: unknown) => {
  const sets = createEmptyValueSets()
  const source = variant as Record<string, unknown> | null | undefined

  if (!source || typeof source !== "object") {
    return sets
  }

  if (source.optionValues && typeof source.optionValues === "object") {
    Object.entries(source.optionValues as Record<string, unknown>).forEach(
      ([optionName, optionValue]) => {
        addOptionValuesToSets(sets, optionName, optionValue)
      }
    )
  }

  if (Array.isArray(source.options)) {
    source.options.forEach((option) => {
      if (option && typeof option === "object") {
        addLegacyVariantOptionToSets(sets, option as Record<string, unknown>)
      }
    })
  }

  if (Array.isArray(source.option_values)) {
    source.option_values.forEach((option) => {
      if (option && typeof option === "object") {
        addLegacyVariantOptionToSets(sets, option as Record<string, unknown>)
      }
    })
  }

  return sets
}

const hasAnyValue = (sets: VariantFilterValueSets) =>
  VARIANT_FILTER_KEYS.some((filterKey) => sets[filterKey].length > 0)

const collectAvailableVariantValueSets = (
  product: unknown
): VariantFilterValueSets[] => {
  const variants = Array.isArray((product as any)?.variants)
    ? ((product as any).variants as unknown[])
    : []

  const availableVariantSets = variants
    .filter((variant) => isVariantAvailable(product, variant))
    .map((variant) => buildValueSetsFromVariant(variant))
    .filter(hasAnyValue)

  if (availableVariantSets.length) {
    return availableVariantSets
  }

  if (!isProductAvailableWithoutVariant(product)) {
    return []
  }

  const fallbackSets = buildValueSetsFromProductOptions(product)
  return hasAnyValue(fallbackSets) ? [fallbackSets] : []
}

export const parseVariantFilterStateFromRecord = (
  input: SearchParamsRecord
): VariantFilterState => {
  const result = createEmptyVariantFilterState()

  VARIANT_FILTER_KEYS.forEach((filterKey) => {
    const entries = splitCommaValues(input[filterKey])
    const map = new Map<string, string>()

    entries.forEach((entry) => {
      const normalized = normalizeText(entry)
      if (!normalized.length || map.has(normalized)) {
        return
      }
      map.set(normalized, entry)
    })

    result[filterKey] = Array.from(map.values())
  })

  return result
}

export const parseVariantFilterStateFromSearchParams = (searchParams: {
  getAll: (name: string) => string[]
}): VariantFilterState => {
  const result = createEmptyVariantFilterState()

  VARIANT_FILTER_KEYS.forEach((filterKey) => {
    const values = searchParams
      .getAll(filterKey)
      .flatMap((entry) => entry.split(","))
      .map((entry) => entry.trim())
      .filter(Boolean)

    const map = new Map<string, string>()
    values.forEach((value) => {
      const normalized = normalizeText(value)
      if (!normalized.length || map.has(normalized)) {
        return
      }
      map.set(normalized, value)
    })

    result[filterKey] = Array.from(map.values())
  })

  return result
}

export const normalizeVariantFilterFacets = (
  input: unknown
): VariantFilterFacets => {
  const source =
    input && typeof input === "object" ? (input as Record<string, unknown>) : {}

  const maps = createEmptyMaps()

  VARIANT_FILTER_KEYS.forEach((filterKey) => {
    const values = splitCommaValues(source[filterKey])
    values.forEach((value) => {
      upsertCaseInsensitive(maps[filterKey], value)
    })
  })

  return {
    talle: Array.from(maps.talle.values()),
    color: Array.from(maps.color.values()),
    material: Array.from(maps.material.values()),
  }
}

export const buildVariantFilterFacetsFromProducts = (
  products: unknown[]
): VariantFilterFacets => {
  const maps = createEmptyMaps()

  products.forEach((product) => {
    collectAvailableVariantValueSets(product).forEach((sets) => {
      VARIANT_FILTER_KEYS.forEach((filterKey) => {
        sets[filterKey].forEach((value) => {
          upsertCaseInsensitive(maps[filterKey], value)
        })
      })
    })
  })

  return {
    talle: Array.from(maps.talle.values()),
    color: Array.from(maps.color.values()),
    material: Array.from(maps.material.values()),
  }
}

export const hasAnyVariantFilterSelection = (state: VariantFilterState) =>
  VARIANT_FILTER_KEYS.some((key) => state[key].length > 0)
